import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { DEFAULT_FILTERS, parseFilters, serializeFilters, type GlobalFilters } from './filters'

type FiltersUpdate = Partial<GlobalFilters> | ((current: GlobalFilters) => GlobalFilters)

/**
 * The global filters, read from and written to the URL. Page-owned query
 * parameters (a funnel `stage`, say) survive a filter change untouched.
 */
export function useGlobalFilters() {
  const [params, setParams] = useSearchParams()
  const search = params.toString()
  const filters = useMemo(() => parseFilters(new URLSearchParams(search)), [search])

  const setFilters = useCallback(
    (update: FiltersUpdate) =>
      setParams((current) => {
        const base = parseFilters(current)
        const next = typeof update === 'function' ? update(base) : { ...base, ...update }
        return serializeFilters(next, current)
      }),
    [setParams],
  )

  const reset = useCallback(
    () => setParams((current) => serializeFilters(DEFAULT_FILTERS, current)),
    [setParams],
  )

  return { filters, setFilters, reset }
}

/** A stable cache key for a filter state: its canonical query string. */
export function filtersKey(filters: GlobalFilters): string {
  return serializeFilters(filters).toString()
}

/** `?range=7d&…` for the current filters only — what a link should carry to keep context. */
export function useFilterSearch(): string {
  const { filters } = useGlobalFilters()
  const query = filtersKey(filters)
  return query ? `?${query}` : ''
}
